import { useMemo } from "react";
import { MetricCompareSelectField, ConditionValues, ConditionScope } from "./fields";
import { Form } from "react-bootstrap";
import { useWatch, useFormContext } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import makeConditionOptions from "./makeConditionOptions";
import { RELATIVE_METRIC_FIELD_ACCESSORS } from "./constants";
import { useProgramContext } from "./ProgramContext";
import { useModuleEnabled } from "../../../redux/selectors/modules";
import { useAppSelector } from "../../../redux/hooks";
import { getCurrencySymbol } from "../../../utils/formatNumber";
import { useTagsQueryOptions } from "../../../modules/advertising/api/useTags";

/*
Conditions

Every-time i sell N units
Every N days
After Jan 1st 2021
Before Jan 1st
Between Jan 1st and Jan 31st
When N total units is sold
When N day average units sold is less than M
When N day average units sold is more than M

*/

const ConditionForm = ({ fieldName, control, divider, conditionIndex = 0, engine = null, isReadOnly = false }) => {
  const ctx = useProgramContext();
  engine = engine || ctx.engine;
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");
  const { marketPlace } = useAppSelector((state) => state.marketPlace);

  const { getValues, resetField, unregister } = useFormContext();

  const selectedCondition = useWatch({
    name: `${fieldName}.option`,
    control,
  });

  const entityType = useWatch({ name: "entity_type", control }) ?? "advertisement";

  const [, accessor] = selectedCondition ? JSON.parse(selectedCondition) : [null, null];
  const isTagCondition = accessor?.includes("tags_enhanced");

  const tagQueryOptions = useTagsQueryOptions({ entity_type: "KEYWORD-TARGET" });
  const { data: tagsData } = useQuery({ ...tagQueryOptions, enabled: isTagCondition && tagsEnhancedEnabled });

  const enhancedTagOptions = useMemo(() => {
    const tags = tagsData?.results ?? [];
    return tags.map((tag) => ({ id: tag.id, label: tag.name, value: tag.id }));
  }, [tagsData?.results]);

  const isMatchTypeAccessor = (a) =>
    a?.includes("target_match_types") || a?.includes("keyword_match_types") || a?.includes("tags_enhanced");

  // Fit the values to the new option: valid match types, or as many numbers as the operator takes, keeping a relative
  // value such as ${max_acos} only for the metric that offers it. A metric without a lookback drops the scope.
  // resetField rather than setValue: it re-runs validation, which setValue doesn't, and replaces the default an
  // emptied input would otherwise fall back to. Inputs the new option drops are unregistered so validation skips them.
  const handleOptionChange = (newOption) => {
    const [newOperator, newMetric] = JSON.parse(newOption);
    const valuesPath = `${fieldName}.values`;
    const current = getValues(valuesPath);
    if (isMatchTypeAccessor(newMetric)) {
      const valid = conditionOptions?.find((o) => o.value === newMetric)?.selectOptions?.map((o) => o.value) ?? [];
      resetField(valuesPath, { defaultValue: Array.isArray(current) ? current.filter((v) => valid.includes(v)) : [] });
    } else if (isMatchTypeAccessor(accessor) || !Array.isArray(current)) {
      // Match types mean nothing as numbers.
      resetField(valuesPath, { defaultValue: [] });
    } else {
      const relativeValues =
        RELATIVE_METRIC_FIELD_ACCESSORS[ctx.engine]
          ?.find((field) => `\${${field.accessor}}` === newMetric)
          ?.options.map((o) => o.value) ?? [];
      const isStaleRelative = (v) => typeof v === "string" && v.startsWith("${") && !relativeValues.includes(v);
      // Blanked rather than dropped, so the input shows empty instead of falling back to its default.
      const kept = current.slice(0, newOperator === "between" ? 2 : 1).map((v) => (isStaleRelative(v) ? "" : v));
      resetField(valuesPath, { defaultValue: kept });
      for (let i = kept.length; i < current.length; i++) unregister(`${valuesPath}.${i}`);
    }
    const hasScope = conditionOptions?.find((o) => o.value === newMetric)?.showScope;
    if (!hasScope && getValues(`${fieldName}.scope`) !== undefined) {
      unregister(`${fieldName}.scope`, { keepDefaultValue: true });
    }
  };
  const conditionOptions = useMemo(() => {
    const options = makeConditionOptions(engine, entityType, { enhancedTagOptions });
    if (engine === "bidding") {
      const hiddenGroups = ["tacos", "troas", "total_cr"];
      if (!tagsEnhancedEnabled) hiddenGroups.push("tags_enhanced");
      return options.filter((o) => !hiddenGroups.includes(o.group));
    }

    return options;
  }, [engine, entityType, enhancedTagOptions, tagsEnhancedEnabled]);

  let { showScope, symbol } = conditionOptions?.find((o) => o.value === accessor) ?? {};

  if (symbol === "currency") {
    symbol = getCurrencySymbol(marketPlace);
  }
  if (symbol === "percent") {
    symbol = "%";
  }

  return (
    <Form.Group>
      <div className={`d-flex align-items-center fs-standard flex-wrap ${divider ? "border-top pt-3" : ""}`}>
        {/* Metric select starts */}
        <MetricCompareSelectField
          fieldName={`${fieldName}.option`}
          control={control}
          rules={{ required: true }}
          metricOptions={conditionOptions}
          isDisabled={isReadOnly}
          onOptionChange={handleOptionChange}
        />
        <ConditionValues
          control={control}
          fieldName={fieldName}
          selectedOption={selectedCondition}
          conditionOptions={conditionOptions}
          accessor={accessor}
          symbol={symbol}
          isDisabled={isReadOnly}
        />
        {showScope && !(engine === "tagging" && conditionIndex > 0) && (
          <ConditionScope control={control} fieldName={fieldName} isDisabled={isReadOnly} />
        )}
      </div>
    </Form.Group>
  );
};

export default ConditionForm;
